import { WuiButton } from '@wui/react';
import { useHistory } from 'react-router-dom';

import accessDeniedSvg from '/assets/svg/error-states/access-denied.svg';
import accountSuspendedSvg from '/assets/svg/error-states/account-suspended.svg';
import pageNotFoundSvg from '/assets/svg/error-states/page-not-found.svg';
import hazardSvg from '/assets/svg/hazard.svg';
import { FullscreenCard } from '/component/chrome/fullscreen/card';
import { useIsAuthenticated } from '/global-state/session/hooks';
import { PageErrorType } from '/module/error/page-error';

import { SomethingWentWrongError } from './states/something-went-wrong';
import css from './style.module.scss';

const TakeMeBackButton = () => {
  const history = useHistory();

  return (
    <WuiButton
      type="secondary"
      label="Take me back"
      onClick={() => history.goBack()}
      data-test="go-back-previous-page"
    />
  );
};

const COPY = {
  permission: {
    title: 'Shields activated',
    message: `Contact your administrator to get access to this page.`,
    image: <img src={accessDeniedSvg} className={css.errorSvg} />,
    button: <TakeMeBackButton />,
  },
  suspended: {
    // Will be changed/improved in upcoming, separate ticket
    title: 'Account suspended',
    message: 'Change your billing settings or contact billing@jwplayer.com.',
    image: <img src={accountSuspendedSvg} className={css.errorSvg} />,
    button: null,
  },
  notFound: {
    title: 'Lost in space',
    message: `The page you're looking for cannot be found.`,
    image: <img src={pageNotFoundSvg} className={css.errorSvg} />,
    button: <TakeMeBackButton />,
  },
  cached: {
    // Used with Showcase
    title: 'Not yet available',
    message: 'Please come back in 10-15 minutes to access this page.',
    image: <img src={hazardSvg} className={css.errorSvg} />,
    button: <TakeMeBackButton />,
  },
  unavailable: {
    title: 'Page unavailable',
    message: 'This page is not available for your account.',
    image: <img src={hazardSvg} className={css.errorSvg} />,
    button: <TakeMeBackButton />,
  },
} as const satisfies Record<
  Exclude<PageErrorType, 'error'>,
  { title: string; message: string; image: React.ReactNode; button: React.ReactNode }
>;

export function PageErrorComponent({ type, customMsg }: { type: PageErrorType; customMsg?: string }) {
  const isAuthed = useIsAuthenticated();

  return (
    <ConditionalWrapper Component={FullscreenCard as React.ComponentType} active={!isAuthed}>
      <div className={css.pageError}>
        {type === 'error' ? (
          <SomethingWentWrongError />
        ) : (
          <>
            <div>{COPY[type].image}</div>
            <h2>{COPY[type].title}</h2>
            <div className={css.message}>{customMsg ?? COPY[type].message}</div>
            <div>{COPY[type].button}</div>
          </>
        )}
      </div>
    </ConditionalWrapper>
  );
}

function ConditionalWrapper({
  active,
  Component,
  children,
}: {
  active: boolean;
  Component: React.ComponentType;
  children: React.ReactChild;
}) {
  if (active) {
    return <Component>{children}</Component>;
  }
  return <>{children}</>;
}
